import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const COLORS = {
  primary: [22, 27, 34],
  accent: [240, 180, 41],
  text: [230, 237, 243],
  muted: [139, 148, 158],
  border: [48, 54, 61]
};

// CORS-safe fetch helper
const getBase64FromUrl = async (url) => {
  if (!url || !url.startsWith('http')) return null;
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
};

const createBaseDoc = (title, dateStr) => {
  const doc = new jsPDF();
  doc.setFont('helvetica');
  doc.setFillColor(13, 17, 23);
  doc.rect(0, 0, 210, 40, 'F');
  
  doc.setFontSize(22);
  doc.setTextColor(240, 180, 41);
  doc.text(title.toUpperCase(), 14, 22);
  
  doc.setFontSize(10);
  doc.setTextColor(139, 148, 158);
  doc.text(dateStr.toUpperCase(), 14, 32);
  
  return doc;
};

const formatCurrency = (val, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(parseFloat(val) || 0);
  } catch (e) {
    return val;
  }
};

// Helper for legacy headers mapping
const formatHeader = (h) => {
  const map = { name: 'Instrument', close: 'Close', change: 'Change', pctChange: 'Change %', notes: 'Notes' };
  return map[h] || h;
};

const drawInstrumentSection = async (doc, item, instrument, startY) => {
  let y = startY;

  // 1. Header (Instrument Info)
  doc.setFillColor(245, 245, 245);
  doc.rect(14, y, 182, 12, 'F');
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(`${instrument.code} - ${instrument.name}`, 18, y + 8);
  y += 20;

  // 2. Price Levels (Critical Data)
  if (instrument.priceLevels?.length > 0) {
    doc.setFontSize(10);
    doc.text("KEY PRICE LEVELS:", 14, y);
    doc.autoTable({
      startY: y + 4,
      head: [['Type', 'Level', 'Context']],
      body: instrument.priceLevels.map(l => [l.type, formatCurrency(l.price, instrument.currency), l.label || '-']),
      theme: 'grid',
      headStyles: { fillColor: [48, 54, 61], textColor: [255, 255, 255] },
      styles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // 3. Tactical Plan (Text Data)
  if (y > 250) {
    doc.addPage();
    y = 20;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text("TACTICAL EXECUTION PLAN:", 14, y);
  doc.setFont('helvetica', 'normal');
  const planLines = doc.splitTextToSize(item.todayPlan || 'No plan specified.', 175);
  doc.text(planLines, 20, y + 8);
  y += (planLines.length * 5) + 15;

  // 4. Analysis Image
  let img = item.imageBase64 || await getBase64FromUrl(item.chartUrl);
  if (img) {
    if (y > 180) {
      doc.addPage();
      y = 20;
    }
    try {
      doc.addImage(img, 'JPEG', 14, y, 182, 90);
      y += 95;
      if (item.chartCaption) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100);
        doc.text(item.chartCaption, 14, y);
        y += 10;
      }
    } catch (e) {
      doc.setTextColor(0, 0, 255);
      doc.textWithLink("View Analysis Chart (External Link)", 20, y, { url: item.chartUrl });
      y += 10;
    }
  } else if (item.chartUrl) {
    doc.setTextColor(0, 0, 255);
    doc.textWithLink("Chart Analysis Link: " + item.chartUrl, 20, y, { url: item.chartUrl });
    y += 10;
  }

  // 5. Trade Log
  if (item.tradeLog?.length > 0) {
    if (y > 240) {
      doc.addPage();
      y = 20;
    }
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text("SESSION TRADE LOG:", 14, y);
    doc.autoTable({
      startY: y + 4,
      head: [['Timestamp', 'Session Note / Action']],
      body: item.tradeLog.map(l => [l.time, l.note]),
      theme: 'striped',
      headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0] },
      styles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  return y;
};

export const exportInFocusOnlyPDF = async (dateStr, items, watchlists) => {
  const doc = createBaseDoc('In-Focus Instruments', dateStr);
  let currentY = 50;

  for (const item of items) {
    const watchlist = watchlists.find(w => w.id === item.watchlistId);
    const instrument = watchlist?.instruments.find(i => i.id === item.instrumentId);
    if (!instrument) continue;

    currentY = await drawInstrumentSection(doc, item, instrument, currentY);
    
    if (currentY > 260) {
      doc.addPage();
      currentY = 20;
    }
  }

  doc.save(`InFocus_${dateStr}.pdf`);
};

export const exportFullDailyPlannerPDF = async (dateStr, dailyData, watchlists) => {
  const doc = createBaseDoc('Terminal Daily Report', dateStr);
  let currentY = 50;

  // 1. Overnight Indices (Dynamic Headers mapping)
  const headers = dailyData.overnightIndicesHeaders || ['name', 'close', 'change', 'pctChange', 'notes'];
  const displayHeaders = headers.map(formatHeader);
  const rows = dailyData.overnightIndices.map(row => headers.map(h => row[h] || ''));

  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text("1. OVERNIGHT INDICES", 14, currentY);
  
  doc.autoTable({
    startY: currentY + 5,
    head: [displayHeaders],
    body: rows,
    theme: 'grid',
    headStyles: { fillColor: [22, 27, 34] },
    styles: { fontSize: 8 },
  });
  currentY = doc.lastAutoTable.finalY + 15;

  // 2. Session Notes
  if (currentY > 250) {
    doc.addPage();
    currentY = 20;
  }
  doc.setFontSize(14);
  doc.text("2. SESSION NOTES", 14, currentY);
  const noteLines = doc.splitTextToSize(dailyData.dailyNotes || 'No notes.', 180);
  doc.setFontSize(9);
  doc.text(noteLines, 14, currentY + 10);
  currentY += (noteLines.length * 5) + 20;

  // 3. Focus Items (Detailed Analysis)
  doc.setFontSize(14);
  doc.text("3. INSTRUMENT ANALYSIS", 14, currentY);
  currentY += 10;

  for (const item of dailyData.inFocusItems || []) {
    const watchlist = watchlists.find(w => w.id === item.watchlistId);
    const instrument = watchlist?.instruments.find(i => i.id === item.instrumentId);
    if (!instrument) continue;

    currentY = await drawInstrumentSection(doc, item, instrument, currentY);
    
    if (currentY > 260) {
      doc.addPage();
      currentY = 20;
    }
  }

  doc.save(`Full_Report_${dateStr}.pdf`);
};

export const exportIndicesPDF = (dateStr, indices, headers) => {
  const doc = createBaseDoc('Overnight Indices', dateStr);
  const fallbackHeaders = ['name', 'close', 'change', 'pctChange', 'notes'];
  const usedHeaders = headers || fallbackHeaders;
  const displayHeaders = usedHeaders.map(formatHeader);
  
  const rows = indices.map(row => usedHeaders.map(h => row[h] || ''));

  doc.autoTable({
    startY: 50,
    head: [displayHeaders],
    body: rows,
    theme: 'grid',
    headStyles: { fillColor: [22, 27, 34] },
  });

  doc.save(`Indices_${dateStr}.pdf`);
};

export const exportNotesPDF = (dateStr, notes) => {
  const doc = createBaseDoc('Daily Notes', dateStr);
  const lines = doc.splitTextToSize(notes || 'No notes for today.', 180);
  doc.text(lines, 14, 50);
  doc.save(`Notes_${dateStr}.pdf`);
};